import {
  Arrival,
  CustomerAnnounceSummary,
  Inventory,
  SalesResultSummary,
} from '@/coreInfrastructureModule/axios/generated/orderControlManage/api';
import {
  IOrderControl,
  IOrderControlArrival,
  IOrderControlCustomerAnnounceSummary,
  IOrderControlInventory,
  IOrderControlResult,
  IOrderControlSalesResultSummary,
} from '../entities/IOrderControl';
import { findOrderStatusOrNull } from '@/coreDomainsModule/entities/arrival';
import { GetOrderBusinessResponseResult } from '@/coreInfrastructureModule/axios/generated/orderManage';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';
import { DateService } from '@/coreDomainsModule/services/dateService';

/**
 *　repositoryの型をUI用に変換する
 */
export class OrderControlAdapter {
  /**
   * IOrderControlResponseをIOrderControlResultに変換する
   * @param response - IOrderControlResponse
   */
  public static adapt = (response: GetOrderBusinessResponseResult): IOrderControlResult => {
    const orderControlBusiness: IOrderControl[] = [];
    const pagingOptions = response.pagingOptions;
    response.data?.forEach((business) => {
      const lastCheckedAt = business.lastCheckedAt;
      const formattedLastCheckedAt =
        lastCheckedAt !== undefined && lastCheckedAt > 0
          ? DateService.convertMSUnixTimeToDate(lastCheckedAt > 1e12 ? lastCheckedAt : lastCheckedAt * 1000)
          : undefined;
      orderControlBusiness.push({
        orderId: business.orderId,
        aggregateBusinessId: business.aggregateBusinessId,
        supplierNumber: business.supplierNumber,
        supplierName: business.supplierName,
        businessNumber: business.businessNumber,
        businessName: business.businessName ?? '',
        package: business.package,
        unit: business.unit,
        manufacturer: business.manufacturer ?? '',
        warehouseNumber: business.warehouseNumber,
        warehouse: business.warehouse,
        createdOrderDate: business.createdOrderDate !== undefined ? business.createdOrderDate / 1000 : undefined,
        desiredDeliveryDate: business.desiredDeliveryDate / 1000,
        orderAmount: business.orderAmount,
        orderStatus: findOrderStatusOrNull(business.orderStatus) ?? 'Unknown',
        leadTime: business.leadTime,
        leadTimeUnit: business.leadTimeUnit ?? '',
        stdInventoryIndicesMin: business.standardInventoryIndexMin,
        stdInventoryIndicesMax: business.standardInventoryIndexMax,
        stdInventoryIndicesUnit: business.standardInventoryIndexUnit,
        lastCheckedAt: formattedLastCheckedAt,
        aggregateBusinessResponsibleUsers:
          business.aggregateBusinessResponsibleUsers?.map((user) => UserAccountFactory.create({ email: user.id, name: user.name })) ?? [],
        purchaseUnitPrice: business.purchaseUnitPrice != null ? Number(business.purchaseUnitPrice) : undefined,
        businessFreeColumnValues: business.businessFreeColumnValues?.map((column) => ({ ...column, target: column.target })) ?? [],
      });
    });
    let useOffset = pagingOptions.nextOffset;
    if (pagingOptions.nextOffset >= pagingOptions.totalResultsAvailable) {
      // 末端処理
      useOffset = pagingOptions.offset;
    }

    return {
      result: orderControlBusiness,
      pagingOptions: {
        pageSize: pagingOptions.pageSize,
        offset: useOffset,
        totalResultsAvailable: pagingOptions.totalResultsAvailable,
        returnCount: pagingOptions.returnCount,
      },
      summaryCount: response.summaryCount,
    };
  };
}

/**
 * バックエンドから返ってくる入荷情報の型から、フロント側の型情報に合わせるためのメソッド
 * @param inventories - バックエンドから受け取る入荷情報
 * @returns IOrderControlInventory[]
 */
const inventoryAdapter = (inventories: Inventory[]): IOrderControlInventory[] => {
  if (inventories.length === 0) return [{ inventoryId: '', year: 0, month: 0, inventoriedDate: 0, amount: 0 }]; // TODO: 何かが違う気がする
  const outputInventories: IOrderControlInventory[] = [];
  for (const inventory of inventories) {
    outputInventories.push({
      inventoryId: inventory.inventoryId ?? '',
      year: inventory.inventoriedTargetYear ?? 0,
      month: inventory.inventoriedTargetMonth ?? 0,
      inventoriedDate: inventory.inventoriedDate ?? 0,
      amount: inventory.inventoryAmount ?? 0,
    });
  }
  return outputInventories;
};

/**
 * バックエンドから返ってくる入荷情報の型から、フロント側の型情報に合わせるためのメソッド
 * @param arrivals - バックエンドから受け取る入荷情報
 * @returns IOrderControlArrival[]
 */
const arrivalAdapter = (arrivals: Arrival[]): IOrderControlArrival[] => {
  if (arrivals.length === 0) return [{ arrivalId: '', year: 0, month: 0, amount: 0 }];
  const outputArrivals: IOrderControlArrival[] = [];
  for (const arrival of arrivals) {
    outputArrivals.push({
      arrivalId: arrival.arrivalId ?? '',
      year: arrival.targetYear ?? 0,
      month: arrival.targetMonth ?? 0,
      amount: arrival.arrivalAmount ?? 0,
    });
  }
  return outputArrivals;
};

/**
 * バックエンドから返ってくる販売実績情報の型から、フロント側の型情報に合わせるためのメソッド
 * @param salesResultSummaries - バックエンドから受け取る販売実績
 * @returns IOrderControlSalesResult[]
 */
const salesResultAdapter = (salesResultSummaries: SalesResultSummary[]): IOrderControlSalesResultSummary[] => {
  if (salesResultSummaries.length === 0) return [{ salesResultSummaryId: '', year: 0, month: 0, amountTotal: 0 }];
  const outputSalesResultSummaries: IOrderControlSalesResultSummary[] = [];
  for (const salesResultSummary of salesResultSummaries) {
    outputSalesResultSummaries.push({
      salesResultSummaryId: salesResultSummary.salesResultIdSummary ?? '',
      year: salesResultSummary.salesResultTargetYear ?? 0,
      month: salesResultSummary.salesResultTargetMonth ?? 0,
      amountTotal: salesResultSummary.salesResultAmountTotal ?? 0,
    });
  }
  return outputSalesResultSummaries;
};

/**
 * バックエンドから返ってくる入荷情報の型から、フロント側の型情報に合わせるためのメソッド
 * @param customerAnnounces - バックエンドから受け取る入荷情報
 * @returns IOrderControlArrival[]
 */
const customerAnnounceAdapter = (customerAnnounces: CustomerAnnounceSummary[]): IOrderControlCustomerAnnounceSummary[] => {
  if (customerAnnounces.length === 0)
    return [{ customerAnnounceSummaryId: '', receivedYear: 0, receivedMonth: 0, year: 0, month: 0, amountTotal: 0 }];
  const outputCustomerAnnounces: IOrderControlCustomerAnnounceSummary[] = [];
  for (const customerAnnounce of customerAnnounces) {
    outputCustomerAnnounces.push({
      customerAnnounceSummaryId: customerAnnounce.customerAnnounceSummaryId ?? '',
      receivedYear: customerAnnounce.customerAnnounceReceivedYear ?? 0,
      receivedMonth: customerAnnounce.customerAnnounceReceivedMonth ?? 0,
      year: customerAnnounce.customerAnnounceTargetYear ?? 0,
      month: customerAnnounce.customerAnnounceTargetMonth ?? 0,
      amountTotal: customerAnnounce.customerForecastAmountTotal ?? 0,
    });
  }
  return outputCustomerAnnounces;
};
